import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelCommentEntity } from '../../domain';
import { usePopupRef } from '@framework/hooks';
import type { ModelCommentThreadOperation } from '../../adapter';
import { ModelCommentDeleteConfirmModal } from '../ModelCommentDeleteConfirmModal';
import { CommentPopupMenuContainer } from './CommentPopupMenuContainer';
import { CommentMenuContainer } from './CommentMenuContainer';
import { CommentMenuButtonContainer } from './CommentMenuButtonContainer';
import type { Point } from '@view-model/models/common/basic';
import { Size } from '@view-model/models/common/basic';
import { CommentMenuPositionMap } from '../../positions';
import { CommentConstants } from '../../constants';
import type { ModelCommentId } from 'balus-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { useRequiredCurrentUserId } from '@framework/auth';

type Props = {
  position: Point;
  comment: ModelCommentEntity;
  isPrimaryComment: boolean;
  threadOperation: ModelCommentThreadOperation;
  onEditStart(id: ModelCommentId): void;
};

export const CommentMenu: React.FC<Props> = ({
  comment,
  position,
  isPrimaryComment,
  threadOperation,
  onEditStart,
}: Props) => {
  const { t } = useTranslation();
  const currentUserId = useRequiredCurrentUserId();
  const isCommentEditable = comment.userId === currentUserId;
  const { isOpen, popupRef, handleClose, handleOpen } = usePopupRef<HTMLDivElement>(false);
  const [commentMenuSize, setCommentMenuSize] = useState(new Size(0, 0));
  const [commentPopupMenuSize, setCommentPopupMenuSize] = useState(new Size(0, 0));

  const handleCommentMenuSizeChange = (newSize: Size) => {
    setCommentMenuSize(newSize);
  };

  const handleCommentPopupMenuSizeChange = (newSize: Size) => {
    setCommentPopupMenuSize(newSize);
  };

  const handleEdit = useCallback(() => {
    handleClose();
    onEditStart(comment.id);
  }, [comment.id, handleClose, onEditStart]);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleDeleteClick = useCallback(() => {
    handleClose();
    setIsDeleteConfirmOpen(true);
  }, [handleClose]);

  const handleDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(false);
    threadOperation.deleteComment(comment.id).then();
  }, [comment.id, threadOperation]);

  const handleDeleteConfirmClose = useCallback(() => {
    setIsDeleteConfirmOpen(false);
  }, []);

  if (!isCommentEditable) return null;

  const commentMenuPositionMap = new CommentMenuPositionMap(
    position,
    commentMenuSize,
    commentPopupMenuSize,
    CommentConstants.UISetting.width
  );

  return (
    <>
      <CommentMenuContainer
        position={commentMenuPositionMap.getMenuButtonPosition()}
        onSizeChange={handleCommentMenuSizeChange}
        paddingTop={40}
      >
        <CommentMenuButtonContainer>
          <button
            type="button"
            className="flex size-10 items-center justify-center rounded text-3xl text-gray-700 hover:bg-gray-300"
            aria-label={t('canvas.comment.menu.open')}
            onClick={handleOpen}
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
        </CommentMenuButtonContainer>
      </CommentMenuContainer>
      {isOpen && (!isPrimaryComment || isCommentEditable) && (
        <CommentMenuContainer
          position={commentMenuPositionMap.getNewPopupMenuPosition()}
          onSizeChange={handleCommentPopupMenuSizeChange}
          paddingTop={40}
        >
          <CommentPopupMenuContainer popupRef={popupRef}>
            <button
              type="button"
              role="menuitem"
              className="rounded-xl px-8 py-2 text-3xl text-white hover:bg-brand-hover"
              onClick={handleEdit}
            >
              {t('canvas.comment.menu.editComment')}
            </button>
            {/* Primary comment の場合はスレッドの削除になるので削除ボタンを表示しない */}
            {!isPrimaryComment && (
              <button
                type="button"
                role="menuitem"
                className="rounded-xl px-8 py-2 text-3xl text-white hover:bg-brand-hover"
                onClick={handleDeleteClick}
              >
                {t('canvas.comment.menu.deleteComment')}
              </button>
            )}
          </CommentPopupMenuContainer>
        </CommentMenuContainer>
      )}
      <ModelCommentDeleteConfirmModal
        isOpen={isDeleteConfirmOpen}
        type="comment"
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteConfirmClose}
      />
    </>
  );
};
